import { graphql } from 'gql.tada';
import { client } from '../client';
import { pdxEvent } from '../../utils/noflake';

// Define the query
const getDeliveryQuery = graphql(`
  query GetDelivery($input: DeliveryInput!) {
    deliveryOptions(input: $input) {
      id
      title
      type
      serviceProvider {
        name
        moniker
        serviceType
        logo {
          url
          altText
        }
      }
      packagingOption
      placeholder {
        id
        display
        logoUrl
        url
      }
      locations {
        openingHours {
          hours
          day
        }
        storeName2
        storeName
        logoUrl
        address {
          provinceCode
          province
          postalCode
          lastName
          firstName
          countryCode
          city
          address2
          address1
        }
        id
        longitude
        latitude
        attributes {
          value
          name
        }
        distance
      }
      description
      fees {
        amount {
          currency
          formattedString
          value
        }
        code
        description
        type
      }
    }
  }
`);

export const getDeliveryQueryFn = async (input: DeliveryVariables['input']) => {
  pdxEvent({
    event_category: 'get_data',
    event_type: 'get_delivery_options',
    event_value: 'delivery_options',
  });

  try {
    const result = await client.request(getDeliveryQuery, { input });

    pdxEvent({
      event_category: 'get_data',
      event_type:
        result.deliveryOptions.length === 0
          ? 'get_delivery_options_empty'
          : 'get_delivery_options_success',
      event_value: 'delivery_options',
    });

    return result;
  } catch (error) {
    pdxEvent({
      event_category: 'error',
      event_type: 'get_delivery_options_error',
      event_value: 'get_data failed to get delivery options',
    });
    throw error;
  }
};

/* eslint-disable typescript/no-explicit-any -- Required for gql.tada type inference */
export type DeliveryVariables =
  typeof getDeliveryQuery extends import('gql.tada').TadaDocumentNode<
    any,
    infer T,
    any
  >
    ? T
    : never;
/* eslint-enable typescript/no-explicit-any */

export type CartItemInputVariables =
  DeliveryVariables['input']['cart']['items'][number];

/* eslint-disable typescript/no-explicit-any -- Required for gql.tada type inference */
type ReturnType =
  typeof getDeliveryQuery extends import('gql.tada').TadaDocumentNode<
    infer R,
    any,
    any
  >
    ? R
    : never;
/* eslint-enable typescript/no-explicit-any */

export type Locations = NonNullable<
  ReturnType['deliveryOptions'][number]['locations']
>;

export type Location = Locations[number];

export type DeliveryOption = ReturnType['deliveryOptions'][number];
export type DeliveryType = DeliveryOption['type'];
export type PackagingOption = DeliveryOption['packagingOption'];
export type Placeholder = DeliveryOption['placeholder'];
